/**
 * Custom hook for managing warehouses with API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { warehousesAPI } from '@/lib/api';
import { useToast } from '@/hooks/useToast';

export function useWarehouses() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => {
      const response = await warehousesAPI.getAll();
      return response.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: warehousesAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      toast({ title: 'Success', description: 'Warehouse created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: warehousesAPI.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      toast({ title: 'Success', description: 'Warehouse updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: warehousesAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      toast({ title: 'Success', description: 'Warehouse deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  return {
    warehouses: data || [],
    isLoading,
    error,
    createWarehouse: createMutation.mutate,
    updateWarehouse: updateMutation.mutate,
    deleteWarehouse: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
